import React, { useState, useEffect, useCallback } from 'react';
import { 
  Wallet, 
  Truck, 
  Calendar, 
  Clock, 
  ShieldCheck, 
  Receipt, 
  X, 
  ChevronRight, 
  User,
  Sparkles,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

import { postData, getData } from '../../../Services/Fetchnodeservices';

const EarningsDashboard = ({ isDark = false }) => {
  const [showWithdrawPopup, setShowWithdrawPopup] = useState(false);
  const [showSettlementPopup, setShowSettlementPopup] = useState(false);
  
  // State for API data
  const [loading, setLoading] = useState(true);
  const [rawOrders, setRawOrders] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [error, setError] = useState(null);
  const [restaurantId, setRestaurantId] = useState(null);

  // Hover states
  const [hoveredBtn, setHoveredBtn] = useState(null);

  // 🎨 ZAPTODOOR ORANGE THEME CONFIGURATION
  const getTheme = () => ({
    bg: isDark ? '#0f172a' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    hoverBg: isDark ? 'rgba(249, 115, 22, 0.1)' : '#ffedd5',
    shadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(249, 115, 22, 0.1)',
    errorBg: isDark ? 'rgba(220, 38, 38, 0.15)' : '#fee2e2',
    errorBorder: isDark ? 'rgba(220, 38, 38, 0.3)' : '#fecaca',
    warningBg: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fff7ed',
    warningBorder: isDark ? 'rgba(245, 158, 11, 0.3)' : '#fed7aa',
    successBg: isDark ? 'rgba(21, 128, 61, 0.15)' : '#dcfce7',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryDark: '#ea580c',
    primaryGradient: isDark 
      ? 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)'
      : 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
  });

  // Get restaurantId from localStorage
  const getRestaurantId = useCallback(() => {
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        const id = userData?.id || userData?.restaurantId || userData?._id;
        if (id) return id;
      }
      const directId = localStorage.getItem('restaurantId');
      if (directId) return directId;
      const userId = localStorage.getItem('userId');
      if (userId) return userId;
      return null;
    } catch (e) {
      console.error('Error getting restaurantId:', e);
      return null;
    }
  }, []);

  // Calculate earnings from orders
  const calculateEarnings = useCallback((orders) => {
    if (!orders || orders.length === 0) return 0;
    const total = orders.reduce((sum, order) => {
      const amount = parseFloat(order.final_amount) || parseFloat(order.business_amount) || parseFloat(order.total_amount) || 0;
      return sum + amount;
    }, 0);
    return total.toFixed(2);
  }, []);

  const getTotalOrders = useCallback((orders) => orders?.length || 0, []);

  const getWalletBalance = useCallback(() => {
    if (eligibility?.walletBalance) return `₹ ${eligibility.walletBalance.toFixed(2)}`;
    return `₹ 0.00`;
  }, [eligibility]);

  const getWithdrawalCycle = useCallback(() => {
    return eligibility?.dayName ? `${eligibility.dayName} (${eligibility.daysRemaining || 0} days)` : '7 Days';
  }, [eligibility]);

  const getNextPayout = useCallback(() => eligibility?.dayName || 'Monday', [eligibility]);
  const canWithdraw = useCallback(() => eligibility?.canWithdraw || false, [eligibility]);
  const getDaysRemaining = useCallback(() => eligibility?.daysRemaining || 0, [eligibility]);

  const getNextWithdrawalDate = useCallback(() => {
    if (eligibility?.nextDate) {
      return new Date(eligibility.nextDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return 'Not available';
  }, [eligibility]);

  const getNextWithdrawalFullDate = useCallback(() => {
    if (eligibility?.nextDate) {
      return new Date(eligibility.nextDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
    }
    return 'Date not available';
  }, [eligibility]);

  const getDayName = useCallback(() => eligibility?.dayName || 'Monday', [eligibility]);
  const getSubscriptionStatus = useCallback(() => subscriptionStatus?.hasSubscription ? 'Active' : 'Inactive', [subscriptionStatus]);
  const getSubscriptionPlan = useCallback(() => subscriptionStatus?.planName || 'No Plan', [subscriptionStatus]);
  
  const getSubscriptionPrice = useCallback(() => {
    if (subscriptionStatus?.price) return `₹ ${subscriptionStatus.price.toFixed(2)}`;
    return '₹ 0.00';
  }, [subscriptionStatus]);

  const getNextDeductionDate = useCallback(() => {
    if (subscriptionStatus?.nextDeductionDate) {
      return new Date(subscriptionStatus.nextDeductionDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return 'No upcoming deduction';
  }, [subscriptionStatus]);

  const getDeductionMessage = useCallback(() => {
    if (subscriptionStatus?.statusMessage) return subscriptionStatus.statusMessage;
    if (subscriptionStatus?.price && subscriptionStatus?.nextDeductionDate) {
      const formattedDate = new Date(subscriptionStatus.nextDeductionDate).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
      return `₹ ${subscriptionStatus.price} will be auto-deducted on ${formattedDate}`;
    }
    return 'No subscription active';
  }, [subscriptionStatus]);

  const isSubscriptionActive = useCallback(() => subscriptionStatus?.hasSubscription || false, [subscriptionStatus]);

  const getSettlementStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'pending' || statusLower === 'processing') return '#d97706';
    if (statusLower === 'completed' || statusLower === 'paid' || statusLower === 'success') return '#15803d';
    if (statusLower === 'failed' || statusLower === 'cancelled') return '#dc2626';
    return '#6b7280';
  };

  const getSettlementStatusLabel = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'pending' || statusLower === 'processing') return 'PENDING';
    if (statusLower === 'completed' || statusLower === 'paid' || statusLower === 'success') return 'COMPLETED';
    if (statusLower === 'failed' || statusLower === 'cancelled') return 'FAILED';
    return status?.toUpperCase() || 'UNKNOWN';
  };

  const formatSettlementDate = (dateString) => {
    if (!dateString) return 'Date not available';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const fetchData = useCallback(async () => {
    const id = getRestaurantId();
    if (!id) {
      setLoading(false);
      setError('Restaurant ID not found. Please login again.');
      return;
    }
    
    setRestaurantId(id);
    setLoading(true);
    setError(null);
    
    try {
      const orderResult = await postData('vanderorders/orders/list', { designation_id: id });
      if (orderResult.success) {
        setRawOrders(orderResult.orders?.filter(o => o.order_status_user === 'delivered') || []);
      }

      const eligibilityResult = await getData(`restaurant/withdrawal-status/${id}`);
      if (eligibilityResult.success) setEligibility(eligibilityResult);

      const paymentResult = await getData(`restaurant/payments/${id}`);
      if (paymentResult.success) {
        const paymentData = paymentResult.data || paymentResult.payments || [];
        setSettlements(Array.isArray(paymentData) ? paymentData : [paymentData]);
      }

      const subResult = await getData(`vanderorders/subscription-status/${id}`);
      if (subResult.success) setSubscriptionStatus(subResult.data);

    } catch (error) {
      console.error("❌ Fetch Error:", error);
      setError(error.message || 'Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [getRestaurantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const theme = getTheme();

  // 🎨 Dynamic Styles based on Orange Theme
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      color: theme.text,
      fontFamily: "'Inter', 'DM Sans', -apple-system, sans-serif",
      paddingBottom: '100px',
      transition: 'background-color 0.3s ease, color 0.3s ease',
    },
    header: {
      padding: '24px 24px 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: '26px',
      fontWeight: 800,
      color: theme.text,
      margin: 0,
      letterSpacing: '-0.5px',
    },
    balanceCard: {
      margin: '24px',
      background: theme.primaryGradient,
      borderRadius: '24px',
      padding: '28px',
      color: '#ffffff',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 20px 40px -10px rgba(249, 115, 22, 0.4)',
    },
    balanceDecorativeCircle: {
      position: 'absolute',
      width: '200px',
      height: '200px',
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.1)',
      right: '-60px',
      top: '-60px',
    },
    balanceLabel: {
      fontSize: '14px',
      color: 'rgba(255,255,255,0.85)',
      marginBottom: '8px',
      fontWeight: 600,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    balanceAmount: {
      fontSize: '42px',
      fontWeight: 800,
      marginBottom: '20px',
      letterSpacing: '-1px',
    },
    balanceActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '16px',
    },
    nextPayout: {
      fontSize: '14px',
      color: 'rgba(255,255,255,0.9)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    withdrawButton: {
      padding: '12px 24px',
      borderRadius: '14px',
      border: 'none',
      background: '#ffffff',
      color: theme.primaryDark,
      fontSize: '15px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '16px',
      padding: '0 24px',
      marginBottom: '24px',
    },
    statCard: (color) => ({
      background: theme.cardBg,
      padding: '20px 16px',
      borderRadius: '20px',
      border: `1px solid ${theme.border}`,
      textAlign: 'center',
      boxShadow: theme.shadow,
      transition: 'transform 0.2s, background-color 0.3s',
      borderTop: `4px solid ${color}`,
    }),
    statIconContainer: (bgColor) => ({
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      backgroundColor: bgColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 12px',
    }),
    statValue: {
      fontSize: '22px',
      fontWeight: 800,
      color: theme.text,
      marginBottom: '4px',
    },
    statLabel: {
      fontSize: '13px',
      color: theme.textSecondary,
      fontWeight: 600,
    },
    subscriptionCard: {
      margin: '0 24px 24px',
      background: theme.cardBg,
      borderRadius: '20px',
      padding: '24px',
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadow,
      position: 'relative',
      overflow: 'hidden',
      transition: 'background-color 0.3s, border-color 0.3s',
    },
    subscriptionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    subscriptionTitle: {
      fontSize: '18px',
      fontWeight: 700,
      color: theme.text,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    subscriptionStatus: {
      padding: '6px 14px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 700,
      background: theme.successBg,
      color: '#15803d',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    subscriptionStatusInactive: {
      padding: '6px 14px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 700,
      background: isDark ? 'rgba(217, 119, 6, 0.15)' : '#fef3c7',
      color: '#d97706',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    subscriptionDetail: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: `1px solid ${theme.border}`,
      fontSize: '14px',
    },
    deductionMessage: {
      marginTop: '16px',
      padding: '14px',
      background: theme.warningBg,
      borderRadius: '14px',
      fontSize: '13px',
      color: isDark ? '#fdba74' : '#9a3412',
      textAlign: 'center',
      fontWeight: 600,
      border: `1px solid ${theme.warningBorder}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'all 0.3s',
    },
    requestButton: {
      margin: '0 24px 32px',
      width: 'calc(100% - 48px)',
      padding: '16px',
      borderRadius: '16px',
      border: 'none',
      background: theme.primaryGradient,
      color: '#ffffff',
      fontSize: '16px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      boxShadow: '0 10px 25px -5px rgba(249, 115, 22, 0.3)',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: 700,
      color: theme.text,
      margin: '0 24px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    settlementCard: {
      margin: '0 24px 12px',
      background: theme.cardBg,
      borderRadius: '16px',
      padding: '20px',
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadow,
      transition: 'all 0.2s ease, background-color 0.3s',
    },
    settlementHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px',
    },
    settlementId: {
      fontSize: '15px',
      fontWeight: 700,
      color: theme.text,
    },
    settlementAmount: {
      fontSize: '18px',
      fontWeight: 800,
      color: theme.text,
    },
    settlementDate: {
      fontSize: '13px',
      color: theme.textSecondary,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    settlementStatus: (status) => ({
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: 700,
      background: status === 'PENDING' ? (isDark ? 'rgba(217, 119, 6, 0.15)' : '#fef3c7') : 
                  status === 'COMPLETED' ? theme.successBg : 
                  status === 'FAILED' ? (isDark ? 'rgba(220, 38, 38, 0.15)' : '#fee2e2') : theme.hoverBg,
      color: status === 'PENDING' ? '#d97706' : 
             status === 'COMPLETED' ? '#15803d' : 
             status === 'FAILED' ? '#dc2626' : theme.textSecondary,
      letterSpacing: '0.5px',
    }),
    overlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(8px)',
      zIndex: 999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    },
    popup: {
      background: theme.cardBg,
      color: theme.text,
      borderRadius: '24px',
      maxWidth: '420px',
      width: '100%',
      padding: '32px',
      position: 'relative',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      animation: 'slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      transition: 'background-color 0.3s',
    },
    popupClose: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      background: theme.hoverBg,
      border: `1px solid ${theme.border}`,
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: theme.textSecondary,
      transition: 'all 0.2s',
    },
    popupIconContainer: {
      width: '64px',
      height: '64px',
      borderRadius: '20px',
      background: isDark ? 'rgba(249, 115, 22, 0.15)' : 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
    },
    popupTitle: {
      fontSize: '22px',
      fontWeight: 800,
      color: theme.text,
      textAlign: 'center',
      marginBottom: '8px',
    },
    popupSubtitle: {
      fontSize: '15px',
      color: theme.textSecondary,
      textAlign: 'center',
      marginBottom: '24px',
      lineHeight: '1.6',
    },
    popupDays: {
      textAlign: 'center',
      padding: '20px',
      background: theme.hoverBg,
      borderRadius: '16px',
      marginBottom: '24px',
      border: `1px dashed ${theme.border}`,
      transition: 'all 0.3s',
    },
    popupButton: {
      width: '100%',
      padding: '16px',
      borderRadius: '14px',
      border: 'none',
      fontSize: '16px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    errorContainer: {
      margin: '20px 24px',
      padding: '16px',
      background: theme.errorBg,
      borderRadius: '12px',
      border: `1px solid ${theme.errorBorder}`,
      color: isDark ? '#fca5a5' : '#dc2626',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '14px',
      fontWeight: 500,
      transition: 'all 0.3s',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: theme.textSecondary,
    },
    emptyStateTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: theme.text,
      marginBottom: '8px',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: theme.bg,
      transition: 'background-color 0.3s',
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: `4px solid ${theme.border}`,
      borderTop: `4px solid ${theme.primary}`,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }
  };

  const getHoverStyle = (btnName) => {
    if (hoveredBtn === btnName) {
      return { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.15)' };
    }
    return {};
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={styles.loadingSpinner} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Earnings</h1>
        <div style={{ 
          width: '40px', height: '40px', borderRadius: '12px', 
          background: theme.hoverBg, border: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.3s'
        }}>
          <User size={20} color={theme.textSecondary} />
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={styles.errorContainer}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Balance Card */}
      <div style={styles.balanceCard}>
        <div style={styles.balanceDecorativeCircle} />
        <div style={styles.balanceLabel}>
          <Wallet size={16} /> AVAILABLE BALANCE
        </div>
        <div style={styles.balanceAmount}>{getWalletBalance()}</div>
        <div style={styles.balanceActions}>
          <span style={styles.nextPayout}>
            <Clock size={14} /> Next Payout: {getNextPayout()}
          </span>
          <button
            style={{
              ...styles.withdrawButton,
              ...(canWithdraw() ? {} : { opacity: 0.6, cursor: 'not-allowed' }),
              ...getHoverStyle('withdraw'),
            }}
            onMouseEnter={() => setHoveredBtn('withdraw')}
            onMouseLeave={() => setHoveredBtn(null)}
            onClick={() => {
              if (canWithdraw()) setShowWithdrawPopup(true);
              else alert(`Withdrawal will be available on ${getNextWithdrawalDate()}`);
            }}
            disabled={!canWithdraw()}
          >
            {canWithdraw() ? 'Withdraw Now' : `In ${getDaysRemaining()} days`} 
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard(theme.primary)}>
          <div style={styles.statIconContainer('rgba(249, 115, 22, 0.1)')}>
            <Truck size={20} color={theme.primary} />
          </div>
          <div style={styles.statValue}>{getTotalOrders(rawOrders)}</div>
          <div style={styles.statLabel}>Deliveries</div>
        </div>
        <div style={styles.statCard('#16a34a')}>
          <div style={styles.statIconContainer('rgba(22, 163, 74, 0.1)')}>
            <Wallet size={20} color="#16a34a" />
          </div>
          <div style={styles.statValue}>{getWalletBalance()}</div>
          <div style={styles.statLabel}>Wallet</div>
        </div>
        <div style={styles.statCard(theme.primaryDark)}>
          <div style={styles.statIconContainer('rgba(234, 88, 12, 0.1)')}>
            <Calendar size={20} color={theme.primaryDark} />
          </div>
          <div style={styles.statValue}>{getWithdrawalCycle()}</div>
          <div style={styles.statLabel}>Cycle</div>
        </div>
      </div>

      {/* Subscription Card */}
      <div style={styles.subscriptionCard}>
        <div style={styles.subscriptionHeader}>
          <span style={styles.subscriptionTitle}>
            <Sparkles size={18} color={theme.primary} /> Your Subscription
          </span>
          <span style={isSubscriptionActive() ? styles.subscriptionStatus : styles.subscriptionStatusInactive}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isSubscriptionActive() ? '#15803d' : '#d97706' }}></span>
            {getSubscriptionStatus()}
          </span>
        </div>
        <div style={styles.subscriptionDetail}>
          <span style={{ color: theme.textSecondary }}>Plan</span>
          <span style={{ color: theme.text, fontWeight: 600 }}>{getSubscriptionPlan()}</span>
        </div>
        <div style={styles.subscriptionDetail}>
          <span style={{ color: theme.textSecondary }}>Price</span>
          <span style={{ color: theme.text, fontWeight: 600 }}>{getSubscriptionPrice()}</span>
        </div>
        <div style={styles.subscriptionDetail}>
          <span style={{ color: theme.textSecondary }}>Next Deduction</span>
          <span style={{ color: theme.text, fontWeight: 600 }}>{getNextDeductionDate()}</span>
        </div>
        {isSubscriptionActive() && (
          <div style={styles.deductionMessage}>
            <ShieldCheck size={16} />
            {getDeductionMessage()}
          </div>
        )}
      </div>

      {/* Request Settlement Button */}
      <button
        style={{ ...styles.requestButton, ...getHoverStyle('request') }}
        onMouseEnter={() => setHoveredBtn('request')}
        onMouseLeave={() => setHoveredBtn(null)}
        onClick={() => setShowSettlementPopup(true)}
      >
        <Receipt size={20} /> Request Settlement Details
      </button>

      {/* Recent Settlements */}
      <h2 style={styles.sectionTitle}>
        <TrendingUp size={20} color={theme.textSecondary} /> Recent Settlements
      </h2>
      
      {settlements.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyStateTitle}>No settlements yet</div>
          <p style={{ fontSize: '14px', color: theme.textSecondary }}>Your settlement history will appear here</p>
        </div>
      ) : (
        settlements.map((settlement, index) => {
          const statusLabel = getSettlementStatusLabel(settlement.payment_status);
          
          return (
            <div 
              key={settlement.id || index} 
              style={{
                ...styles.settlementCard,
                ...(hoveredBtn === `settlement-${index}` ? { transform: 'translateY(-2px)', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' } : {})
              }}
              onMouseEnter={() => setHoveredBtn(`settlement-${index}`)}
              onMouseLeave={() => setHoveredBtn(null)}
            >
              <div style={styles.settlementHeader}>
                <span style={styles.settlementId}>
                  {settlement.restaurant_name || `Settlement #${settlement.id || index + 1}`}
                </span>
                <span style={styles.settlementAmount}>
                  ₹ {parseFloat(settlement.total_amount || 0).toFixed(2)}
                </span>
              </div>
              <div style={{ ...styles.settlementHeader, marginTop: '8px' }}>
                <span style={styles.settlementDate}>
                  <Clock size={12} /> {formatSettlementDate(settlement.created_at)}
                </span>
                <span style={styles.settlementStatus(statusLabel)}>
                  {statusLabel}
                </span>
              </div>
              {settlement.total_orders && (
                <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '8px', display: 'flex', gap: '16px' }}>
                  <span>Orders: {settlement.total_orders}</span>
                  {settlement.payment_date && <span>Payment: {new Date(settlement.payment_date).toLocaleDateString()}</span>}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Withdraw Popup */}
      {showWithdrawPopup && (
        <div style={styles.overlay} onClick={() => setShowWithdrawPopup(false)}>
          <div style={styles.popup} onClick={(e) => e.stopPropagation()}>
            <button style={styles.popupClose} onClick={() => setShowWithdrawPopup(false)}>
              <X size={20} />
            </button>

            <div style={styles.popupIconContainer}>
              <Clock size={32} color={isDark ? '#fdba74' : '#ea580c'} />
            </div>
            <h2 style={styles.popupTitle}>Almost There!</h2>
            <p style={styles.popupSubtitle}>
              Vendor security ke liye, aap har <span style={{ color: theme.primary, fontWeight: 700 }}>{getWithdrawalCycle()}</span> mein hi withdrawal kar sakte hain.
            </p>

            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', color: theme.textSecondary, marginBottom: '4px' }}>Next Withdrawal Opening</div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: theme.text }}>{getNextWithdrawalFullDate()}</div>
              <div style={{ fontSize: '14px', color: theme.primary, fontWeight: 600 }}>{getDayName()}</div>
            </div>

            <div style={styles.popupDays}>
              <div style={{ fontSize: '36px', fontWeight: 800, color: theme.text, lineHeight: 1 }}>
                {getDaysRemaining()}
              </div>
              <div style={{ fontSize: '12px', color: theme.textSecondary, fontWeight: 700, letterSpacing: '1px', marginTop: '4px' }}>
                DAYS REMAINING
              </div>
            </div>

            <button
              style={{
                ...styles.popupButton,
                background: theme.primaryGradient,
                color: '#ffffff',
                ...getHoverStyle('popupBtn'),
              }}
              onMouseEnter={() => setHoveredBtn('popupBtn')}
              onMouseLeave={() => setHoveredBtn(null)}
              onClick={() => setShowWithdrawPopup(false)}
            >
              Got it, Thanks!
            </button>
          </div>
        </div>
      )}

      {/* Settlement Popup */}
      {showSettlementPopup && (
        <div style={styles.overlay} onClick={() => setShowSettlementPopup(false)}>
          <div style={styles.popup} onClick={(e) => e.stopPropagation()}>
            <button style={styles.popupClose} onClick={() => setShowSettlementPopup(false)}>
              <X size={20} />
            </button>

            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ 
                width: '64px', height: '64px', borderRadius: '20px', 
                background: isDark ? 'rgba(21, 128, 61, 0.15)' : 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'
              }}>
                <ShieldCheck size={32} color={isDark ? '#4ade80' : '#15803d'} />
              </div>
              <h2 style={styles.popupTitle}>Subscription Details</h2>
              <p style={{ fontSize: '14px', color: theme.textSecondary }}>Your current active plan overview</p>
            </div>

            <div style={{ background: theme.hoverBg, borderRadius: '16px', padding: '20px', marginBottom: '24px', border: `1px solid ${theme.border}` }}>
              {[
                { label: 'Plan Name', value: getSubscriptionPlan() },
                { label: 'Status', value: getSubscriptionStatus(), isStatus: true },
                { label: 'Price', value: getSubscriptionPrice() },
                { label: 'Next Deduction', value: getNextDeductionDate() },
                { label: 'Billing Cycle', value: subscriptionStatus?.planName === 'Monthly' ? 'Monthly' : 'N/A' },
                { label: 'Start Date', value: subscriptionStatus?.lastDeductionDate ? new Date(subscriptionStatus.lastDeductionDate).toLocaleDateString() : 'Not available' },
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: idx < 5 ? `1px solid ${theme.border}` : 'none' }}>
                  <span style={{ color: theme.textSecondary, fontSize: '14px' }}>{item.label}</span>
                  <span style={{ 
                    color: item.isStatus ? (isSubscriptionActive() ? '#15803d' : '#d97706') : theme.text, 
                    fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    {item.isStatus && (
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isSubscriptionActive() ? '#15803d' : '#d97706' }}></span>
                    )}
                    {item.value}
                  </span>
                </div>
              ))}
            </div>

            {isSubscriptionActive() && (
              <div style={styles.deductionMessage}>
                <ShieldCheck size={16} />
                {getDeductionMessage()}
              </div>
            )}

            <button
              style={{
                ...styles.popupButton,
                background: theme.hoverBg,
                color: theme.text,
                border: `1px solid ${theme.border}`,
                marginTop: '20px',
                ...getHoverStyle('closePopup'),
              }}
              onMouseEnter={() => setHoveredBtn('closePopup')}
              onMouseLeave={() => setHoveredBtn(null)}
              onClick={() => setShowSettlementPopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EarningsDashboard;